import { getKeycloak } from '../keycloak';
import { logger } from 'helpers/logger';
import { parseApiResponse } from '../types/apiTypes';
import { EncryptStorage } from 'encrypt-storage';

const encryptStorage = new EncryptStorage('secret-key-value', { prefix: '@mwan' });

const KC_OFFLINE_TOKEN_KEY = 'kc_offline_token';

export const storeKeycloakOfflineToken = (keycloak) => {
    if (keycloak.refreshToken) encryptStorage.setItem(KC_OFFLINE_TOKEN_KEY, keycloak.refreshToken);
};

export const getStoredOfflineToken = () => encryptStorage.getItem(KC_OFFLINE_TOKEN_KEY);

export const makeAuthenticatedRequest = async (url, options = {}) => {
    const isOffline = !navigator.onLine;
    let token;

    if (isOffline) {
        // Offline: use whatever token is in memory — request will fail at the network layer
        // and the SW will queue it in the outbox. The outbox saga refreshes the token before replaying.
        try {
            const keycloak = getKeycloak();
            token = keycloak.token;
        } catch {
            token = null;
        }

        if (!token) {
            throw new Error('No authentication token available offline');
        }
    } else {
        const keycloak = getKeycloak();

        if (!keycloak.authenticated) {
            logger.warn('⚠️ User not authenticated');
            keycloak.login();
            throw new Error('User not authenticated');
        }

        logger.info('🕐 Current time:', new Date().toISOString());
        logger.info('🕐 Token expires at:', new Date(keycloak.tokenParsed.exp * 1000).toISOString());
        logger.info('⏱️ Time until expiry (seconds):', keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000));

        try {
            const refreshed = await keycloak.updateToken(30);
            if (refreshed) {
                logger.info('✅ Token WAS refreshed');
                logger.info('🆕 New token expires at:', new Date(keycloak.tokenParsed.exp * 1000).toISOString());
                storeKeycloakOfflineToken(keycloak);
            } else {
                logger.info('✅ Token was NOT refreshed (still valid)');
            }
        } catch (error) {
            logger.error('❌ Token refresh failed:', error);
            keycloak.login();
            throw new Error('Authentication failed');
        }

        token = keycloak.token;
    }

    const defaultHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    logger.info('🔑 Auth headers:', defaultHeaders);

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };

    logger.info('📡 Request URL:', url);
    logger.info('📡 Request config:', config);

    const response = await fetch(url, config);

    // SW returns a 202 synthetic response when a mutation is queued offline
    if (response.status === 202) {
        const text = await response.text();
        return text ? JSON.parse(text) : { queued: true };
    }

    const text = await response.text();
    const json_response = text
        ? parseApiResponse(JSON.parse(text))
        : parseApiResponse({ statusCode: response.status, message: response.statusText });

    if (json_response.statusCode < 200 || json_response.statusCode >= 300) {
        const errorsDetail = json_response.errors?.length
            ? `\nErrors:\n${json_response.errors.join('\n')}`
            : '';
        throw new Error(`HTTP error! status: ${json_response.statusCode}\nHTTP error message: ${json_response.message}${errorsDetail}`);
    }

    return json_response;
};
